import { type TopicIdentityRecord } from "@/shared/domain/topics";
import {
  HttpClient,
  type PaginatedResponse,
  robotoHeaders,
} from "@/shared/http";
import { parseTimestampsAsBigInt } from "@/shared/json";

import { type SessionFile } from "./SessionFile";
import { type SessionRecord, type UpdateSessionRequest } from "./SessionRecord";

interface MutateOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Resource-Owner-Id` header. Required for callers
   * whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_OWNER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  resourceOwnerId?: string;
}

interface GetOptions {
  abortSignal?: AbortSignal;
  /**
   * Sets the `X-Roboto-Resource-Owner-Id` header. Required for callers
   * whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_OWNER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  resourceOwnerId?: string;
}

interface ListOptions {
  abortSignal?: AbortSignal;
  pageToken?: string;
  /**
   * Sets the `X-Roboto-Resource-Owner-Id` header. Required for callers
   * whose Cognito identity is a member of more than one org — the
   * backend `REQUIRES_OWNER_ORG` rule rejects ambiguous requests with
   * `RobotoNoOrgProvidedException`.
   */
  resourceOwnerId?: string;
}

/**
 * HTTP client for the `/v1/sessions` API. Construct once per
 * `HttpClient`; resolved by feature code through the
 * `DomainServicesProvider` context.
 */
export class SessionService {
  #httpClient: HttpClient;

  constructor(httpClient: HttpClient) {
    this.#httpClient = httpClient;
  }

  public async deleteSession(
    sessionId: string,
    options?: MutateOptions,
  ): Promise<void> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.delete(url, {
      idempotent: false,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    await response.throwIfError();
  }

  public async getSessionById(
    sessionId: string,
    options?: GetOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.get(url, {
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }

  public async listSessionFiles(
    sessionId: string,
    options?: ListOptions,
  ): Promise<PaginatedResponse<SessionFile>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/files`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<SessionFile>>(
      parseTimestampsAsBigInt,
    );
  }

  public async listSessionTopics(
    sessionId: string,
    options?: ListOptions,
  ): Promise<PaginatedResponse<TopicIdentityRecord>> {
    const searchParams = new URLSearchParams();
    if (options?.pageToken !== undefined) {
      searchParams.append("page_token", options.pageToken);
    }
    const url = this.#httpClient.constructUrl(
      `v1/sessions/id/${sessionId}/topics`,
      searchParams,
    );
    const response = await this.#httpClient.get(url, {
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      signal: options?.abortSignal,
    });
    return response.json<PaginatedResponse<TopicIdentityRecord>>();
  }

  public async updateSession(
    sessionId: string,
    updates: UpdateSessionRequest,
    options?: MutateOptions,
  ): Promise<SessionRecord> {
    const url = this.#httpClient.constructUrl(`v1/sessions/id/${sessionId}`);
    const response = await this.#httpClient.put(url, {
      idempotent: true,
      headers: options?.resourceOwnerId
        ? robotoHeaders({ resourceOwnerId: options.resourceOwnerId })
        : undefined,
      body: JSON.stringify(updates),
      signal: options?.abortSignal,
    });
    return response.json<SessionRecord>(parseTimestampsAsBigInt);
  }
}
